import React, { useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import { ChevronIcon } from 'shared/assets/chevron-icon';
import { PinLocationIcon } from 'src/assets/pin-location-icon';
import { Typography } from 'src/typography';
import { tracker } from 'utils/analytics';

import { useOtherLocations } from './use-other-locations';
import type { OtherLocation } from './use-other-locations';

/** Rows visible before the shopper asks for the rest. */
const PEEK_COUNT = 3;

type OtherLocationsProps = {
  product?: { enterpriseProductId?: string | null } | null;
  dispensary?: { id?: string | null; retailer?: { enterpriseId?: string | null } | null } | null;
};

function formatDistance(distanceMiles?: number | null): string | null {
  if (typeof distanceMiles !== 'number' || !Number.isFinite(distanceMiles)) {
    return null;
  }

  return distanceMiles < 0.1 ? 'Less than 0.1 mi' : `${distanceMiles} mi`;
}

/**
 * A chain can stock a product at dozens of stores, so the count carries the message and the
 * list stays short: the three nearest are shown and the rest are behind a toggle. `50` is the
 * resolver's cap, so a bigger chain reads as "50+" rather than silently under-reporting.
 */
function summaryText(count: number, capped: boolean): string {
  const label = capped ? `${count}+` : `${count}`;
  return count === 1 ? 'In stock at 1 other location' : `In stock at ${label} other locations`;
}

function LocationCard({ location }: { location: OtherLocation }): JSX.Element {
  const distance = formatDistance(location.distanceMiles);

  return (
    <CardLink
      href={location.productUrl}
      data-testid={`other-location-${location.dispensaryId}`}
      onClick={() =>
        tracker.dispensaryClicked({
          dispensaryId: location.dispensaryId,
          dispensaryName: location.name,
          dispensaryAddress: location.address ?? undefined,
          dispensaryChainName: location.chain ?? undefined,
          source: 'pdp-other-locations',
          meta: { product: { id: location.productId } },
        })
      }
    >
      <CardBody>
        <LocationName size='small' tag='div'>
          {location.name}
        </LocationName>
        <Meta size='small' tag='div'>
          {distance && <Distance data-testid='other-location-distance'>{distance}</Distance>}
          {distance && location.address && <Dot aria-hidden='true'>·</Dot>}
          {location.address && <Address>{location.address}</Address>}
        </Meta>
      </CardBody>
      <CardChevron aria-hidden='true' />
    </CardLink>
  );
}

/**
 * PDP section listing the other locations in this enterprise where the product is in stock,
 * nearest to the store being viewed first. Renders nothing unless the product belongs to an
 * enterprise, the LD flag is on, and at least one other location has it available.
 */
export function OtherLocations({ product, dispensary }: OtherLocationsProps): JSX.Element | null {
  const { locations, loading, enabled, capped } = useOtherLocations({ product, dispensary });
  const [expanded, setExpanded] = useState(false);

  // Render nothing until we know there is something to show. A heading + spinner would
  // advertise availability we haven't confirmed, then collapse again when the answer is
  // "nowhere else" — a wasted promise and a layout shift on every enterprise PDP.
  if (!enabled || loading || locations.length === 0) {
    return null;
  }

  const hiddenCount = locations.length - PEEK_COUNT;
  const visible = expanded ? locations : locations.slice(0, PEEK_COUNT);

  return (
    <Container data-testid='other-locations'>
      <Heading tag='h2' size='medium'>
        Also available at
      </Heading>

      <Summary data-testid='other-locations-summary'>
        <PinLocationIcon />
        <SummaryText size='small' tag='span'>
          {summaryText(locations.length, capped)}
        </SummaryText>
      </Summary>

      <LocationList>
        {visible.map((location) => (
          <li key={location.dispensaryId}>
            <LocationCard location={location} />
          </li>
        ))}
      </LocationList>

      {hiddenCount > 0 && (
        <ToggleRow>
          <Toggle
            type='button'
            data-testid='other-locations-toggle'
            aria-expanded={expanded}
            onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
          >
            {expanded ? 'Show fewer locations' : `Show ${hiddenCount} more location${hiddenCount === 1 ? '' : 's'}`}
            <ToggleChevron $expanded={expanded} aria-hidden='true' />
          </Toggle>
        </ToggleRow>
      )}
    </Container>
  );
}

const Container = styled.div`
  border-top: 1px solid var(--dcm-product-other-locations-divider-color, ${({ theme }) => theme.colors.blueGrey[95]});
  padding-top: 24px;
  margin-bottom: 32px;
`;

const Heading = styled(Typography.Heading)`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 16px;
  color: var(--dcm-product-other-locations-summary-color, ${({ theme }) => theme.colors.grey[30]});

  svg {
    flex-shrink: 0;
  }
`;

const SummaryText = styled(Typography.Body)`
  font-size: 14px;
  font-weight: 600;
`;

const LocationList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const CardLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--dcm-product-other-locations-card-border-color, ${({ theme }) => theme.colors.blueGrey[95]});
  border-radius: var(--dcm-product-other-locations-card-radius, var(--dct--customized-radius-lg));
  text-decoration: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: var(--dcm-product-other-locations-card-hover-border-color, ${({ theme }) => theme.colors.grey[70]});
    background-color: var(--dcm-product-other-locations-card-hover-bg, ${({ theme }) => theme.colors.grey[95]});
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const LocationName = styled(Typography.Body)`
  color: var(--dcm-product-other-locations-name-color, ${({ theme }) => theme.colors.primaryBlack});
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled(Typography.Body)`
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  color: var(--dcm-product-other-locations-meta-color, ${({ theme }) => theme.colors.grey[45]});
  font-size: 13px;
  line-height: 1.4;
`;

const Distance = styled.span`
  font-weight: 600;
  white-space: nowrap;
`;

const Dot = styled.span`
  flex-shrink: 0;
`;

const Address = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardChevron = styled(ChevronIcon)`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  transform: rotate(-90deg);
  color: var(--dcm-product-other-locations-chevron-color, ${({ theme }) => theme.colors.grey[60]});
`;

const ToggleRow = styled.div`
  display: flex;
  margin-top: 12px;
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--dcm-product-other-locations-toggle-color, var(--dct--customized-colors-buttonsLinks));
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
`;

const ToggleChevron = styled(ChevronIcon)<{ $expanded: boolean }>`
  width: 10px;
  height: 10px;
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'none')};
`;
